import React, { lazy, Suspense, useCallback, useEffect, useState } from "react";
import HeaderWithAdmin from "@/components/HeaderWithAdmin";
import Meta from "@/components/Meta";
import Hero from "@/components/Hero";
import AffiliatePromoBanner from "@/components/AffiliatePromoBanner";
import { CommunityPopup } from "@/components/Community/CommunityPopup";
import { FloatingSocialWidget } from "@/components/SocialShare";


const Features = lazy(() => import("@/components/Features"));
const FeaturedProducts = lazy(() => import("@/components/FeaturedProducts"));
const Testimonials = lazy(() => import("@/components/Testimonials"));
const Newsletter = lazy(() => import("@/components/Newsletter"));
const CTA = lazy(() => import("@/components/CTA"));
const FAQ = lazy(() => import("@/components/FAQ"));
const BenefitsComparison = lazy(() => import("@/components/BenefitsComparison"));
const Footer = lazy(() => import("@/components/Footer"));

const LoadingPlaceholder = () => (
  <div className="h-20 bg-gaming-dark/50 animate-pulse rounded-md" />
);

const faqs = [
  {
    question: "How long does it take to build and ship a custom PC?",
    answer: "Our custom PCs typically take 7–10 business days..."
  },
  {
    question: "What warranty do you offer on your custom PCs?",
    answer: "All BattleforgePC systems come with a comprehensive 3-year warranty..."
  },
  {
    question: "Can I upgrade my PC later on?",
    answer: "Absolutely! All our systems are built using standard components..."
  },
  {
    question: "Do you offer international shipping?",
    answer: "Yes, we ship to most international locations..."
  },
  {
    question: "What payment methods do you accept?",
    answer: "We accept all major credit cards, PayPal, bank transfers, and crypto..."
  }
];

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqs.map(faq => ({
    "@type": "Question",
    "name": faq.question,
    "acceptedAnswer": {
      "@type": "Answer",
      "text": faq.answer
    }
  }))
};

const productSchema = {
  "@context": "https://schema.org",
  "@type": "Product",
  "name": "BattleforgePC Custom Gaming Computers",
  "description": "High-performance custom gaming PCs built with premium components",
  "brand": {
    "@type": "Brand",
    "name": "BattleforgePC"
  },
  "offers": {
    "@type": "AggregateOffer",
    "lowPrice": "999.00",
    "highPrice": "4999.00",
    "priceCurrency": "USD",
    "offerCount": "10",
    "availability": "https://schema.org/InStock"
  },
  "aggregateRating": {
    "@type": "AggregateRating",
    "ratingValue": "4.8",
    "reviewCount": "150"
  }
};

const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "BattleforgePC",
  "url": "https://battleforgepc.com",
  "logo": "https://battleforgepc.com/logo.png",
  "contactPoint": {
    "@type": "ContactPoint",
    "telephone": "+1-555-123-4567",
    "contactType": "customer service",
    "availableLanguage": "English"
  },
  "sameAs": [
    "https://www.facebook.com/battleforgepc",
    "https://twitter.com/battleforgepc",
    "https://www.instagram.com/battleforgepc",
    "https://www.youtube.com/channel/battleforgepc",
    "https://www.linkedin.com/company/battleforgepc",
    "https://discord.gg/XvMxcv8UbK"
  ]
};

const Index = () => {



  const pageDescription = "Transform your gaming experience with BattleforgePC's cutting-edge custom computers, tailored for elite performance and stunning aesthetics. Build your dream rig today.";

  console.log("[PRERENDER-DEBUG /] Index component is rendering.");

  return (
    <div className="min-h-screen bg-gaming-darker">
      <Meta
        title="BattleforgePC | Custom Gaming PCs & High-Performance Computers"
        description={pageDescription}
        keywords="gaming pc, custom pc, high performance computer, gaming rig, custom built pc, gaming computer, rtx gaming pc, battleforge pc, prebuilt gaming pc, custom workstation"
        canonicalUrl="https://battleforgepc.com"
        ogImage="https://battleforgepc.com/your-homepage-image.png"
        jsonLdSchemas={[faqSchema, productSchema, organizationSchema]}
      />

      <HeaderWithAdmin />
      <CommunityPopup />
      <FloatingSocialWidget />

      <main>
        <Hero />
        <AffiliatePromoBanner />
        <Suspense fallback={<LoadingPlaceholder />}><Features /></Suspense>
        <Suspense fallback={<LoadingPlaceholder />}><FeaturedProducts /></Suspense>
        <Suspense fallback={<LoadingPlaceholder />}><BenefitsComparison /></Suspense>
        <Suspense fallback={<LoadingPlaceholder />}><Testimonials /></Suspense>
        <Suspense fallback={<LoadingPlaceholder />}><FAQ /></Suspense>
        <Suspense fallback={<LoadingPlaceholder />}><CTA /></Suspense>
        <Suspense fallback={<LoadingPlaceholder />}><Newsletter /></Suspense>
      </main>

      <Suspense fallback={<LoadingPlaceholder />}><Footer /></Suspense>
    </div>
  );
};

export default Index;
